"use client"

import React, { lazy, Suspense } from "react"
import { MapPin } from "lucide-react"
import { Fade } from "@/components/ui/fade"

// Lazy load the heavy Map component (includes maplibre-gl)
const MapBlockInner = lazy(() =>
	import("./MapBlockInner").then((mod) => ({ default: mod.MapBlockInner })),
)

export interface MapBlockBlock {
	title?: string | null
	subtitle?: string | null
	center: {
		longitude: number
		latitude: number
	}
	zoom?: number | null
	height?: "300px" | "400px" | "500px" | "600px" | null
	markers?:
		| {
				label: string
				longitude: number
				latitude: number
				id?: string | null
		  }[]
		| null
	showControls?: boolean | null
	id?: string | null
	blockName?: string | null
	blockType: "mapBlock"
}

function MapFallback({ height }: { height: string }) {
	return (
		<div
			className="flex items-center justify-center bg-muted animate-pulse rounded-xl"
			style={{ height }}
		>
			<MapPin className="h-8 w-8 text-muted-foreground/40" />
		</div>
	)
}

export const MapBlockComponent: React.FC<MapBlockBlock> = ({
	title,
	subtitle,
	center,
	zoom = 14,
	height = "400px",
	markers,
	showControls = true,
}) => {
	const mapHeight = height ?? "400px"

	return (
		<section className="bg-white py-16 dark:bg-neutral-950">
			<div className="container mx-auto px-4">
				{(title || subtitle) && (
					<Fade className="mb-10 text-center">
						{title && (
							<h2 className="mb-4 text-3xl font-bold text-black dark:text-white">
								{title}
							</h2>
						)}
						{subtitle && (
							<p className="mx-auto max-w-2xl text-lg text-gray-600 dark:text-gray-400">
								{subtitle}
							</p>
						)}
					</Fade>
				)}

				<Fade delay={200}>
					<div
						className="overflow-hidden rounded-xl border border-gray-200 shadow-lg dark:border-neutral-800"
						style={{ height: mapHeight }}
					>
						<Suspense fallback={<MapFallback height={mapHeight} />}>
							<MapBlockInner
								center={center}
								zoom={zoom}
								markers={markers}
								showControls={showControls}
							/>
						</Suspense>
					</div>
				</Fade>
			</div>
		</section>
	)
}
